import { z } from "zod";
import { paginated, paginationQuery } from "./common.js";
export const studyKind = z.enum(["exclusive", "sectorial"]);
export const studyStatus = z.enum(["draft", "published", "archived"]);
const periodRefinement = (value, ctx) => {
    if (value.periodStart && value.periodEnd && value.periodStart > value.periodEnd) {
        ctx.addIssue({
            code: "custom",
            message: "El periodo inicial no puede ser posterior al final",
            path: ["periodEnd"],
        });
    }
};
/**
 * La forma base, sin refinar. Los dos contratos se derivan de aquí en vez de
 * uno del otro: `.superRefine()` ya devuelve un esquema con comprobaciones
 * colgadas, y extenderlo es terreno resbaladizo. Extender lo plano y refinar
 * cada resultado no lo es.
 */
const studyFields = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).default(""),
    category: z.string().trim().max(100).nullish(),
    industry: z.string().trim().max(100).nullish(),
    periodStart: z.iso.date().optional(),
    periodEnd: z.iso.date().optional(),
    /**
     * Texto libre a propósito (no fecha): el analista captura "la próxima
     * semana", "el próximo mes" o "en cuanto esté disponible la información",
     * no siempre una fecha exacta.
     */
    nextUpdate: z.string().trim().max(200).nullish(),
    /**
     * Vacío o ausente al crear, la API pone el nombre de quien crea el
     * insight. Se puede corregir: no siempre sube el reporte quien lo hizo.
     */
    author: z.string().trim().max(120).nullish(),
    kind: studyKind.default("sectorial"),
    featured: z.boolean().default(false),
    tags: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
});
export const createStudyInput = studyFields.superRefine(periodRefinement);
/** El alta nace siempre en borrador; solo la edición puede mover el estado. */
export const updateStudyInput = studyFields
    .extend({ status: studyStatus.optional() })
    .superRefine(periodRefinement);
export const studySummary = z.object({
    id: z.uuid(),
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    category: z.string().nullable(),
    industry: z.string().nullable(),
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    nextUpdate: z.string().nullable(),
    author: z.string().nullable(),
    kind: studyKind,
    status: studyStatus,
    /**
     * Derivado, no almacenado: el estudio no está concedido a ninguna
     * organización interna. Es lo que el candado del catálogo comunica.
     */
    restricted: z.boolean(),
    featured: z.boolean(),
    coverImageUrl: z.string().nullable(),
    hasContent: z.boolean(),
    htmlBytes: z.number().int().nullable(),
    publishedAt: z.string().nullable(),
    /** Última modificación del registro; la tarjeta la muestra como "Últ. actualización". */
    updatedAt: z.string(),
    tags: z.array(z.string()),
    /** Solo se rellena para ir_admin; vacío para el resto (spec §5). */
    organizations: z.array(z.string()),
    /**
     * true si el estudio tiene al menos una concesión directa a un usuario
     * -sin importar si también tiene organizaciones-. Igual que
     * `organizations`, solo se calcula para ir_admin; `false` para el resto.
     * Existe para que el listado distinga "sin ninguna organización, pero con
     * usuarios puntuales" de "nadie puede verlo": `organizations` vacío por sí
     * solo no alcanza para esa distinción.
     */
    hasUserAccess: z.boolean(),
});
export const studyList = paginated(studySummary);
export const studyListQuery = paginationQuery.extend({
    q: z.string().trim().max(200).optional(),
    category: z.string().trim().optional(),
    industry: z.string().trim().optional(),
    tag: z.string().trim().optional(),
    status: studyStatus.optional(),
    featured: z.coerce.boolean().optional(),
    sort: z.enum(["recent", "title"]).default("recent"),
});
/**
 * Categorías e industrias en uso, para que el formulario del insight
 * sugiera valores sin cerrar el vocabulario (spec §6.1, §7.1).
 */
export const studyFacets = z.object({
    categories: z.array(z.string()),
    industries: z.array(z.string()),
});
/** Organizaciones y usuarios con concesión directa sobre un estudio. */
export const studyAccessView = z.object({
    organizationIds: z.array(z.string()),
    userIds: z.array(z.string()),
});
/**
 * Reemplaza el conjunto completo de concesiones (spec §6.1): declarativo a
 * propósito, porque el formulario que lo consume (Task 13) conoce el estado
 * final, no el diferencial.
 */
export const setStudyAccessInput = z.object({
    organizationIds: z.array(z.string().min(1)).max(200),
    userIds: z.array(z.string().min(1)).max(200),
});
//# sourceMappingURL=study.js.map